import { ReactElement, ReactNode } from 'react';

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import DownloadIcon from '@mui/icons-material/Download';

export interface GratiTableColumn<T> {
  field: string;
  getValue: (row: T) => string | number | boolean;
  renderCell?: (row: T, rowIndex: number) => ReactNode; // Custom cell renderer (overrides getValue display)
  isInteractive?: boolean; // Excluded from CSV/XLSX template downloads
  width?: number; // Optional column width
  align?: 'left' | 'center' | 'right'; // Optional alignment
  group?: string; // Optional group label for 2-row header
  excelFormula?: string; // Optional Excel formula for XLSX template (use {ROW} for row number)
  excelValidation?: { type: 'list'; formulae: string[] }; // Optional data validation for XLSX template
}

export interface GratiTableRow<T> {
  field: string;
  dbname: keyof T;
  example: string;
  required: boolean;
  leagueDefaultKey?: string;
  group?: string; // Optional group label for 2-row header
  excelFormula?: string; // Optional Excel formula for XLSX template (use {ROW} for row number)
  excelValidation?: { type: 'list'; formulae: string[] }; // Optional data validation for XLSX template
}

interface GratiCsvTableProps<T> {
  description?: string;
  isEditable?: boolean;
  columns: GratiTableColumn<T>[];
  rows: T[];
  onRowChange?: (index: number, updatedRow: T) => void;
  hasDownloadAction?: boolean;
}

/** Build consecutive group spans from column groups for the header row. */
function buildGroupSpans<T>(columns: GratiTableColumn<T>[]): Array<{ group: string; span: number }> {
  const spans: Array<{ group: string; span: number }> = [];
  for (const col of columns) {
    const group = col.group ?? '';
    if (spans.length > 0 && spans[spans.length - 1].group === group) {
      spans[spans.length - 1].span++;
    } else {
      spans.push({ group, span: 1 });
    }
  }
  return spans;
}

export default function GratiCsvTable<T>({
  description = '',
  columns,
  isEditable: _isEditable = false,
  rows,
  onRowChange: _onRowChange,
  hasDownloadAction = false,
}: GratiCsvTableProps<T>): ReactElement {
  const hasGroups = columns.some((col) => col.group);

  // Columns eligible for CSV/XLSX template downloads (exclude interactive-only columns)
  const downloadColumns = columns.filter((col) => !col.isInteractive);

  const getTemplateData = (): [(string | number | boolean)[], (string | number | boolean)[]] => {
    // Get headers (excluding first column which is the "Property" label column)
    const headers = downloadColumns.slice(1).map((col) => col.field);

    // Get example row (where first column value is 'Example')
    const exampleRow = rows.find((row) => downloadColumns[0].getValue(row) === 'Example');
    const exampleData = exampleRow ? downloadColumns.slice(1).map((col) => col.getValue(exampleRow)) : [];

    return [headers, exampleData];
  };

  /** Build group row for template downloads (excluding the first "Property" column). */
  const getGroupRow = (): string[] => {
    const dataColumns = downloadColumns.slice(1);
    const spans = buildGroupSpans(dataColumns);
    const groupRow: string[] = [];
    for (const { group, span } of spans) {
      groupRow.push(group);
      for (let i = 1; i < span; i++) {
        groupRow.push('');
      }
    }
    return groupRow;
  };

  const handleCsvDownload = () => {
    const [headers, exampleData] = getTemplateData();
    const csvRows: string[] = [];
    if (hasGroups) {
      csvRows.push(getGroupRow().join(','));
    }
    csvRows.push(headers.join(','));
    csvRows.push(exampleData.join(','));

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'event_template.csv');

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleXlsxDownload = async () => {
    const ExcelJS = await import('exceljs');
    const [headers, exampleData] = getTemplateData();
    const dataColumns = downloadColumns.slice(1);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template');

    let currentRowNum = 1; // ExcelJS uses 1-based row numbers

    // Group header row (optional)
    if (hasGroups) {
      const groupRow = getGroupRow();
      const wsRow = worksheet.addRow(groupRow);
      wsRow.eachCell((cell) => {
        if (cell.value) {
          cell.font = { bold: true };
          cell.alignment = { horizontal: 'center' };
        }
      });

      // Merge group header cells
      const spans = buildGroupSpans(dataColumns);
      let colStart = 1;
      for (const { span } of spans) {
        if (span > 1) {
          worksheet.mergeCells(currentRowNum, colStart, currentRowNum, colStart + span - 1);
        }
        colStart += span;
      }
      currentRowNum++;
    }

    // Column header row — styled with blue background + white bold text
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'center' };
    });
    currentRowNum++;

    // Data (example) row
    const dataRow = worksheet.addRow(exampleData);
    const dataRowNum = currentRowNum;

    // Apply Excel formulas (overrides static example values)
    dataColumns.forEach((col, colIndex) => {
      if (col.excelFormula) {
        const resolvedFormula = col.excelFormula.replace(/\{ROW\}/g, String(dataRowNum));
        dataRow.getCell(colIndex + 1).value = { formula: resolvedFormula };
      }
    });

    // Apply data validations (dropdowns)
    dataColumns.forEach((col, colIndex) => {
      if (col.excelValidation) {
        dataRow.getCell(colIndex + 1).dataValidation = col.excelValidation;
      }
    });

    // Set column widths based on header length (min 12 chars)
    worksheet.columns = headers.map((h) => ({
      width: Math.max(String(h).length + 2, 12),
    }));

    // Write and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'event_template.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const groupSpans = hasGroups ? buildGroupSpans(columns) : null;

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        alignItems: "flex-start",
        justifyContent: "flex-start"
      }}>
      {description && <Typography sx={{ mb: 2 }}>{description}</Typography>}
      <TableContainer component={Paper}>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            minWidth: () => `${columns.length * 150}px`,
          }}
        >
          <TableHead>
            {groupSpans && (
              <TableRow>
                {groupSpans.map((g, i) => (
                  <TableCell
                    key={i}
                    colSpan={g.span}
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      borderBottom: g.group ? undefined : 'none',
                    }}
                  >
                    {g.group}
                  </TableCell>
                ))}
              </TableRow>
            )}
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    width: col.width,
                    textAlign: col.align || 'left',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  {col.field}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => {
              const hasBooleans = columns.some((col) => typeof col.getValue(row) === 'boolean');
              return (
                <TableRow key={rowIndex} sx={hasBooleans ? { backgroundColor: 'action.hover' } : undefined}>
                  {columns.map((col, colIndex) => (
                    <TableCell
                      key={`${rowIndex}-${col.field}`}
                      className={colIndex === 0 ? 'firstColumn' : undefined}
                      sx={{
                        width: col.width,
                        textAlign: col.align || 'left',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                      }}
                    >
                      {col.renderCell
                        ? col.renderCell(row, rowIndex)
                        : typeof col.getValue(row) === 'boolean'
                          ? (col.getValue(row) ? 'Yes' : 'No')
                          : col.getValue(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {hasDownloadAction && (
        <Stack direction="row" spacing={1} sx={{ alignSelf: 'flex-end' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleCsvDownload} size="small">
            Download template as CSV file
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleXlsxDownload} size="small">
            Download template as Excel file
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
